import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Bar } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
} from 'chart.js';
import { Helmet } from 'react-helmet';

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
);

const StandardDeviationCalculator = () => {
  const [inputData, setInputData] = useState({
    numbers: '',
    mean: '',
    variance: '',
    standardDeviation: ''
  });

  const [results, setResults] = useState(null);
  const [error, setError] = useState('');

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setInputData(prev => ({
      ...prev,
      [name]: value
    }));
    setError('');
  };

  const calculateStandardDeviation = () => {
    const { numbers } = inputData;

    // Validate input
    if (!numbers) {
      setError('Please enter a list of numbers');
      return;
    }

    // Parse and validate numbers
    const numberList = numbers.split(',').map(num => num.trim());
    const validNumbers = numberList.map(num => {
      const parsedNum = parseFloat(num);
      if (isNaN(parsedNum)) {
        setError('Please enter a valid list of numbers (comma-separated)');
        return null;
      }
      return parsedNum;
    }).filter(num => num !== null);

    if (validNumbers.length === 0) {
      setError('Please enter a valid list of numbers (comma-separated)');
      return;
    }

    // Calculate mean
    const sum = validNumbers.reduce((acc, num) => acc + num, 0);
    const mean = sum / validNumbers.length;

    // Calculate variance
    const variance = validNumbers.reduce((acc, num) => acc + Math.pow(num - mean, 2), 0) / validNumbers.length;

    // Calculate standard deviation
    const standardDeviation = Math.sqrt(variance);

    // Create visualization data
    const labels = validNumbers.map((_, i) => `Number ${i + 1}`);
    const chartData = {
      labels,
      datasets: [{
        label: 'Number Value',
        data: validNumbers,
        backgroundColor: validNumbers.map(() => 'rgba(75, 192, 192, 0.5)'),
        borderColor: validNumbers.map(() => 'rgba(75, 192, 192, 1)'),
        borderWidth: 1
      }]
    };

    setResults({
      mean,
      variance,
      standardDeviation,
      chartData
    });
  };

  const exampleData = {
    numbers: '10, 20, 30, 40, 50',
    mean: '30',
    variance: '200',
    standardDeviation: '14.14'
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 1.5 }}
      className="min-h-screen bg-gradient-to-r from-purple-100 to-pink-100"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <Helmet>
          <title>Standard Deviation Calculator | Statistical Tool</title>
          <meta name="description" content="Calculate the standard deviation of a list of numbers. This calculator helps in statistical analysis by providing insights into data variability." />
          <meta name="keywords" content="standard deviation calculator, statistics, variance, mean, data analysis" />
        </Helmet>

        <div className="relative overflow-hidden bg-white rounded-lg shadow-2xl p-8 mb-8">
          <div className="calculator-header flex items-center mb-8">
            <div className="header-icon text-4xl text-pink-600 mr-4">📊</div>
            <h1 className="text-3xl font-bold text-gray-800">Standard Deviation Calculator</h1>
          </div>

          <div className="text-gray-600 mb-8">
            <p>Calculate the standard deviation of a list of numbers. This calculator helps in statistical analysis by providing insights into data variability.</p>
          </div>

          <div className="grid grid-cols-1 gap-6 mb-6">
            <div className="input-group">
              <label className="block text-sm font-medium text-gray-700 mb-2">Numbers (comma-separated):</label>
              <input
                type="text"
                name="numbers"
                value={inputData.numbers}
                onChange={handleInputChange}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-pink-500 focus:border-transparent"
                placeholder="Enter numbers (e.g., 10, 20, 30, 40, 50)"
              />
            </div>
          </div>

          <button
            onClick={calculateStandardDeviation}
            className="w-full py-3 px-4 bg-pink-600 text-white font-medium rounded-lg hover:bg-pink-700 focus:outline-none focus:ring-2 focus:ring-pink-500 focus:ring-offset-2 transition-colors duration-200"
          >
            Calculate Standard Deviation
          </button>

          {error && (
            <div className="mt-4 p-4 text-red-700 bg-red-100 rounded-lg">
              {error}
            </div>
          )}

          {results && (
            <div className="mt-8">
              <h2 className="text-2xl font-bold text-gray-800 mb-6">Results</h2>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
                <div className="bg-gray-50 p-6 rounded-lg">
                  <h3 className="text-lg font-semibold text-gray-700 mb-4">Statistical Analysis</h3>
                  <p className="text-lg">Mean: {results.mean.toFixed(2)}</p>
                  <p className="text-lg">Variance: {results.variance.toFixed(2)}</p>
                  <p className="text-lg">Standard Deviation: {results.standardDeviation.toFixed(2)}</p>
                </div>

                <div className="bg-gray-50 p-6 rounded-lg">
                  <h3 className="text-lg font-semibold text-gray-700 mb-4">Data Summary</h3>
                  <p className="text-lg">Number of Data Points: {results.chartData.labels.length}</p>
                  <p className="text-lg">Sum of Data: {results.chartData.datasets[0].data.reduce((a, b) => a + b, 0)}</p>
                </div>
              </div>

              {results.chartData && (
                <div className="bg-white p-6 rounded-lg shadow-md mb-8">
                  <Bar
                    data={results.chartData}
                    options={{
                      responsive: true,
                      maintainAspectRatio: false,
                      scales: {
                        y: {
                          beginAtZero: true,
                          title: {
                            display: true,
                            text: 'Number Value'
                          }
                        },
                        x: {
                          title: {
                            display: true,
                            text: 'Data Point'
                          }
                        }
                      },
                      plugins: {
                        title: {
                          display: true,
                          text: 'Number Values'
                        }
                      }
                    }}
                  />
                </div>
              )}
            </div>
          )}
        </div>

        {/* Example Calculation Section */}
        <div className="calculator-section mt-12">
          <h2 className="text-2xl font-bold text-gray-800 mb-6">Example Calculation <span className="text-blue-600">📝</span></h2>
          <div className="text-gray-600">
            <p>Here's an example calculation for standard deviation:</p>
            
            <div className="bg-white p-6 rounded-lg shadow-md mt-4">
              <h4 className="mb-4">Sample Data:</h4>
              <ul className="list-disc pl-6 mb-4">
                <li>Input Numbers: {exampleData.numbers}</li>
                <li>Mean: {exampleData.mean}</li>
                <li>Variance: {exampleData.variance}</li>
                <li>Standard Deviation: {exampleData.standardDeviation}</li>
              </ul>

              <h4 className="mb-4">Calculation Method:</h4>
              <ol className="list-decimal pl-6 mb-4">
                <li>Calculate the mean of the numbers</li>
                <li>Calculate the variance by averaging the squared differences from the mean</li>
                <li>Take the square root of the variance to get the standard deviation</li>
              </ol>
            </div>
          </div>
        </div>

        <div className="calculator-section mt-12">
          <h2 className="text-2xl font-bold text-gray-800 mb-6">How to Use <span className="text-blue-600">📋</span></h2>
          <div className="text-gray-600">
            <p>Follow these steps to use the Standard Deviation Calculator:</p>
            <ol className="list-decimal pl-6 mt-4">
              <li><strong>Enter Numbers:</strong>
                <ul className="list-disc pl-6 mt-2">
                  <li>Input your list of numbers (comma-separated)</li>
                </ul>
              </li>
              <li><strong>Click Calculate:</strong> The calculator will determine:
                <ul className="list-disc pl-6 mt-2">
                  <li>The mean of your numbers</li>
                  <li>The variance of your numbers</li>
                  <li>The standard deviation of your numbers</li>
                </ul>
              </li>
              <li><strong>Interpret Results:</strong>
                <ul className="list-disc pl-6 mt-2">
                  <li>Review the calculated mean, variance, and standard deviation</li>
                  <li>View the visual representation of the numbers</li>
                </ul>
              </li>
            </ol>
            <div className="mt-4 p-4 bg-blue-100 rounded-lg">
              <p><strong>💡 Pro Tips:</strong></p>
              <ul className="list-disc pl-6 mt-2">
                <li>Standard deviation measures the spread of data points</li>
                <li>Higher standard deviation indicates more variability</li>
                <li>Useful for statistical analysis and data interpretation</li>
              </ul>
            </div>
          </div>
        </div>

        <div className="calculator-section mt-12">
          <h2 className="text-2xl font-bold text-gray-800 mb-6">Understanding Standard Deviation <span className="text-blue-600">💻</span></h2>
          <div className="text-gray-600">
            <p>
              Standard deviation is a measure of the amount of variation or dispersion in a set of values. It helps in understanding how spread out the numbers are from the mean. Key points:
            </p>
            <ul className="list-disc pl-6 mt-4">
              <li><strong>Low Standard Deviation:</strong> Data points are close to the mean</li>
              <li><strong>High Standard Deviation:</strong> Data points are spread out over a wider range</li>
              <li><strong>Use Cases:</strong> Financial analysis, quality control, scientific research</li>
            </ul>
          </div>
        </div>

        <div className="calculator-section mt-12">
          <h2 className="text-2xl font-bold text-gray-800 mb-6">Best Practices <span className="text-blue-600">✨</span></h2>
          <div className="text-gray-600">
            <ul className="list-disc pl-6 mt-4">
              <li><strong>Accuracy:</strong> Double-check your input numbers</li>
              <li><strong>Documentation:</strong> Record your calculations</li>
              <li><strong>Validation:</strong> Verify results when critical</li>
              <li><strong>Implementation:</strong> Follow statistical guidelines</li>
            </ul>
            <p className="italic mt-4">
              Note: Always verify your calculations, especially when using statistical data for critical decision-making.
            </p>
          </div>
        </div>
      </div>
    </motion.div>
  );
};

export default StandardDeviationCalculator;